import React from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  useTheme,
  alpha,
  Fade,
  SxProps,
  Theme
} from '@mui/material';
import { TrendingUp, TrendingDown, Equalizer, Tune } from '@mui/icons-material';

interface MetricCardProps {
  title: string;
  value: string | number;
  subtitle: string;
  icon: React.ReactElement;
  color?: string;
  trend?: 'up' | 'down' | 'stable';
  trendValue?: string;
  delay?: number;
}

// Enhanced MetricCard component with better icon handling
const MetricCard: React.FC<{
  title: string;
  value: string | number;
  subtitle: string;
  icon: React.ReactElement;
  color?: string;
  trend?: 'up' | 'down' | 'stable';
  trendValue?: string;
  delay?: number;
}> = ({ title, value, subtitle, icon, color = '#1976d2', trend, trendValue, delay = 0 }) => {
  const theme = useTheme();
  
  // Determine color based on trend if not explicitly provided
  // let finalColor = color;
  // if (trend === 'up') finalColor = theme.palette.success.main;
  // if (trend === 'down') finalColor = theme.palette.error.main;
  // if (trend === 'stable') finalColor = theme.palette.warning.main;
  


  const finalColor =
  color ||
  (trend === 'up'
    ? theme.palette.success.main
    : trend === 'down'
    ? theme.palette.error.main
    : trend === 'stable'
    ? theme.palette.warning.main
    : theme.palette.primary.main);
  // Get appropriate trend icon
  const getTrendIcon = () => {
    if (trend === 'up') return <TrendingUp sx={{ fontSize: 16 }} />;
    if (trend === 'down') return <TrendingDown sx={{ fontSize: 16 }} />;
    if (trend === 'stable') return <Tune sx={{ fontSize: 16 }} />;
    return null;
  };
  
  // Card hover animation styles
  const cardSx: SxProps<Theme> = {
    height: '100%',
    borderRadius: 2,
    boxShadow: 2,
    transition: 'all 0.3s ease-in-out',
    background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(
      theme.palette.background.paper,
      0.9
    )} 100%)`,
    position: 'relative',
    overflow: 'hidden',
    '&:before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '4px',
      background: `linear-gradient(90deg, ${finalColor}, ${alpha(finalColor, 0.5)})`,
    },
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: `0 6px 20px 0 ${alpha(finalColor, 0.2)}`,
    }
  };
  
  return (
    <Fade in={true} timeout={800} style={{ transitionDelay: `${delay}ms` }}>
      <Card sx={cardSx}>
        <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
          <Box display="flex" alignItems="flex-start" justifyContent="space-between">
            <Box flex={1}>
              <Typography 
                color="text.secondary" 
                gutterBottom 
                variant="overline" 
                fontWeight="500"
                sx={{ opacity: 0.8, fontSize: '0.75rem' }}
              >
                {title}
              </Typography>
              
              <Typography 
                variant="h3" 
                component="div" 
                fontWeight="700"
                sx={{ 
                  color: finalColor,
                  mb: 1,
                  background: `linear-gradient(45deg, ${finalColor}, ${alpha(finalColor, 0.8)})`,
                  backgroundClip: 'text',
                  textFillColor: 'transparent',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                {value}
              </Typography>
              
              <Box display="flex" alignItems="center" flexWrap="wrap">
                {trend && (
                  <Box
                    display="flex"
                    alignItems="center"
                    sx={{
                      color: finalColor,
                      mr: 1,
                      borderRadius: 1,
                      px: 0.5,
                      py: 0.25,
                      backgroundColor: alpha(finalColor, 0.1)
                    }}
                  >
                    {getTrendIcon()}
                    {trendValue && (
                      <Typography variant="caption" sx={{ ml: 0.5, fontWeight: '600' }}>
                        {trendValue}
                      </Typography>
                    )}
                  </Box>
                )}
                
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{ fontSize: '0.875rem' }}
                >
                  {subtitle}
                </Typography>
              </Box>
            </Box>
            
            <Box
              sx={{
                color: finalColor,
                backgroundColor: alpha(finalColor, 0.1),
                borderRadius: 2,
                p: 1.5,
                ml: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}
            >
              {React.cloneElement(icon, { 
                sx: { 
                  fontSize: 28,
                  color: finalColor
                } 
              })}
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Fade>
  );
};

export default MetricCard;